/**
 * The office app's routed shell.
 *
 * `AppFrame` draws the chrome and takes a nav key; the router decides which
 * page is inside it. Keeping the frame outside the `Routes` means moving
 * between Contacts and a contact does not remount the header, the nav or the
 * scope selector — only the page.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { NAV_ITEMS, visibleNavItems, type MeResponse, type NavKey } from '@rowted/shared'
import { AppFrame } from '../frame/app-frame.js'
import { useActivityNavBadge } from '../activity/nav-badge.js'
import { EmptyState } from '../components/primitives.js'
import { Contacts } from './contacts.js'
import { Properties } from './properties.js'
import { JobsBoard } from './jobs-board.js'
import { JobRecord } from './job-record.js'
import { NewJob } from './new-job.js'
import { QuoteRoute } from './quote-route.js'
import { CrewBoard } from './crew-board.js'
import { ContactRecord } from './contact-record.js'
import { PropertyRecord } from './property-record.js'
import { SettingsShell } from '../settings/settings-shell.js'
import { Account } from './account.js'
import { Purchasing } from './purchasing.js'
import { Invoices } from './invoices.js'
import { Visits } from './visits.js'
import { PurchaseOrders } from './purchase-orders.js'
import { Equipment } from './equipment.js'
import { EquipmentRecord } from './equipment-record.js'
import { InvoiceRecord } from './invoice-record.js'
import { VisitPage } from '../visit/visit-page.js'
import { EquipmentAssignment } from './equipment-assignment.js'
import { ActivityPage } from '../activity/activity-page.js'
/*
 * The record forms. One each, for a record that exists and for one that does
 * not — there is no separate "New …" component any more, because a contact
 * created from this button had five fields and the same contact edited a
 * minute later had seventeen.
 */
import { ContactForm } from '../components/forms/contact-form.js'
import { PropertyForm } from '../components/forms/property-form.js'
import { JobForm } from '../components/forms/job-form.js'
import {
  NewRequestSheet, type NewRequestPrefill, type RequestOutcome,
} from '../requests/new-request-sheet.js'
import { NEW_REQUEST_EVENT, REQUEST_CREATED_EVENT } from '../requests/open-request.js'
import { RequestToast } from '../requests/request-toast.js'
import { usePhone } from '../lib/use-narrow.js'
import { EquipmentForm } from '../components/forms/equipment-form.js'
import { request, setScope } from '../lib/api.js'

/**
 * Which nav item the current path belongs to, so the rail highlights it.
 *
 * Some screens are reached through another section and have no rail entry of
 * their own — a quote is opened from a job and belongs under Jobs. Without
 * this the rail fell back to the first visible item, so building a quote
 * highlighted Contacts.
 */
const SECTION_OWNERS: Record<string, NavKey> = {
  quotes: 'jobs',
}

function activeNavKey(pathname: string, fallback: NavKey): NavKey | null {
  const segment = pathname.split('/')[1] ?? ''
  const match = NAV_ITEMS.find((item) => item.key === segment)
  if (match) return match.key
  if (SECTION_OWNERS[segment]) return SECTION_OWNERS[segment]!
  // Pages with no rail entry of their own highlight nothing. The home page is
  // the one exception: it is where the rail's first item lives.
  return segment === '' ? fallback : null
}

export function Office({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const location = useLocation()

  const nav = visibleNavItems(me.permissions, { officeApp: true })
  /* Activity's green count (§13.2): unread conversations and tasks due or late. */
  const activityBadge = useActivityNavBadge(nav.some((item) => item.key === 'activity'))
  const first = nav[0]?.key ?? 'jobs'

  // The create forms live here rather than inside each list, so "New contact"
  // works the same from the Contacts page and from anywhere else that needs it.
  const [creating, setCreating] = useState<
    'contact' | 'property' | 'job' | 'equipment' | 'request' | null>(null)
  /** What New request starts with: a record's ⋯ (§13.3), or book mode (§11.4). */
  const [prefill, setPrefill] = useState<NewRequestPrefill | null>(null)
  /** The nav's Create menu, which the **N** key opens (§13.4). */
  const [createOpen, setCreateOpen] = useState(false)
  /** Bumped after New request creates, so the list on screen shows it. */
  const [reloadSignal, setReloadSignal] = useState(0)
  const [toast, setToast] = useState<(RequestOutcome & { key: number }) | null>(null)
  const phone = usePhone()
  const canAddContacts = me.permissions.contacts === 'edit' || me.permissions.contacts === 'full'
  const canAddProperties = me.permissions.properties === 'edit'
    || me.permissions.properties === 'full'
  const canAddJobs = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'
  const canAddEquipment = me.permissions.equipment === 'edit'
    || me.permissions.equipment === 'full'

  const startRequest = useCallback((start: NewRequestPrefill | null = null) => {
    setCreateOpen(false)
    setPrefill(start)
    setCreating('request')
  }, [])

  /*
   * **N** opens the nav's Create menu, and **R** in it chooses New request — so
   * N then R opens it from anywhere. With no nav on screen (a phone) N R opens
   * the sheet straight away. Never while typing, never with a modifier (§13.4).
   */
  const waitingForR = useRef<number | null>(null)
  useEffect(() => {
    if (!canAddJobs) return
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      if (target && (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
        || target.isContentEditable)) return
      if (creating) return
      const key = event.key.toLowerCase()
      if (key === 'n' && !event.shiftKey) {
        if (phone) {
          waitingForR.current = window.setTimeout(() => { waitingForR.current = null }, 1500)
        } else {
          event.preventDefault()
          setCreateOpen(true)
        }
      } else if (key === 'r' && waitingForR.current !== null) {
        window.clearTimeout(waitingForR.current)
        waitingForR.current = null
        event.preventDefault()
        startRequest()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canAddJobs, creating, phone, startRequest])

  /* A record's ⋯ and the job's *Book a time* ask for the sheet with this event (§13.3, §11.4). */
  useEffect(() => {
    const onAsk = (event: Event) => startRequest((event as CustomEvent<NewRequestPrefill>).detail ?? null)
    window.addEventListener(NEW_REQUEST_EVENT, onAsk)
    return () => window.removeEventListener(NEW_REQUEST_EVENT, onAsk)
  }, [startRequest])

  async function signOut() {
    await request('/auth/sign-out', { method: 'POST' }).catch(() => {})
    window.location.assign('/sign-in')
  }

  async function chooseScope(orgUnitIds: string[]) {
    // Validated first — the server refuses a unit this role cannot reach, and
    // remembering one it refused would narrow every list to nothing.
    // `scope: orgUnitIds` so the check is not itself narrowed by the old one.
    await request('/me/scope', { method: 'POST', body: { orgUnitIds }, scope: orgUnitIds })
    setScope(orgUnitIds)
    // The scope is part of every list query, so the simplest correct thing is
    // to reload rather than invalidate each screen's state by hand.
    window.location.reload()
  }

  return (
    <AppFrame
      me={me}
      active={activeNavKey(location.pathname, first)}
      badges={activityBadge ? { activity: activityBadge } : undefined}
      onNavigate={(key) => navigate(`/${key}`)}
      onSignOut={signOut}
      onScopeChange={(ids) => void chooseScope(ids)}
      onCreate={canAddJobs ? () => startRequest() : undefined}
      createOpen={createOpen}
      onCreateOpenChange={setCreateOpen}
    >
      <Routes>
        {/*
          * **The bare URL goes to the first nav item** — Activity, where the
          * day starts (`docs/activity.md` §13.1; it was Contacts until there
          * was a page worth landing on). `replace`, so the back button goes
          * where it came from rather than bouncing off the redirect.
          */}
        <Route path="/" element={<Navigate to={`/${first}`} replace />} />
        <Route path="/activity" element={<ActivityPage me={me} />} />
        <Route
          path="/contacts"
          element={
            <Contacts
              me={me}
              onNew={canAddContacts ? () => setCreating('contact') : undefined}
              onNewRequest={canAddJobs ? () => startRequest() : undefined}
              reloadSignal={reloadSignal}
            />
          }
        />
        <Route path="/contacts/:id" element={<ContactRecord me={me} />} />
        <Route
          path="/properties"
          element={
            <Properties
              me={me}
              onNew={canAddProperties ? () => setCreating('property') : undefined}
              onNewRequest={canAddJobs ? () => startRequest() : undefined}
              reloadSignal={reloadSignal}
            />
          }
        />
        <Route path="/properties/:id" element={<PropertyRecord me={me} />} />
        <Route
          path="/jobs"
          element={(
            <JobsBoard
              me={me}
              onNew={canAddJobs ? () => setCreating('job') : undefined}
              onNewRequest={canAddJobs ? () => startRequest() : undefined}
              reloadSignal={reloadSignal}
            />
          )}
        />
        {/*
          * `/jobs/new` before `/jobs/:id`: the router ranks a literal segment
          * above a param whatever the order, but a reader should not have to
          * know that to see why "new" does not load a job.
          */}
        <Route path="/jobs/new" element={<NewJob canCreate={canAddJobs} />} />
        <Route path="/jobs/:id" element={<JobRecord me={me} />} />
        <Route path="/quotes/:id" element={<QuoteRoute me={me} />} />
        {/* Work visits (addendum §E.7). A list and nothing else: scheduling
            makes visits, the crew board moves them, the iPad closes them. */}
        <Route path="/visits" element={<Visits />} />
        {/* The Visit page (docs/visit-page.md): a visit and every day of it; a day's id lands on its visit. */}
        <Route path="/visits/:id" element={<VisitPage />} />
        <Route path="/schedule" element={<CrewBoard me={me} />} />
        {/*
          * Settings — everything a company sets up once and then lives with,
          * in one shell with its own nav (`docs/catalog-and-packages.md`
          * §6.1). Before this, People hung off the account menu, the catalog
          * hung off a different part of it, and "where do I change how we do
          * things" had no answer.
          */}
        <Route path="/settings/*" element={<SettingsShell me={me} />} />
        {/*
          * Everything about you, which is not a company setting and so does
          * not live under `/settings`. The user menu's first row.
          */}
        <Route path="/account" element={<Account me={me} />} />
        {/*
          * The three old addresses. They were the whole answer for a year and
          * they are in people's history and their bookmarks, so they land
          * where the thing moved to rather than on a blank page.
          */}
        <Route path="/people" element={<Navigate to="/settings/people" replace />} />
        <Route path="/catalog" element={<Navigate to="/settings/catalog" replace />} />
        <Route
          path="/catalog/items"
          element={<Navigate to="/settings/catalog?tab=items" replace />}
        />
        {/* Phase 6 (§6.8). Morning assignment is a screen of its own rather
            than a tab: it is the yard's day, and the yard opens it directly.
            It is listed before `/equipment/:id` for readers' sake — the
            router ranks a literal segment above a param whatever the order. */}
        <Route path="/equipment" element={
          <Equipment me={me} onNew={canAddEquipment ? () => setCreating('equipment') : undefined} />
        } />
        <Route path="/equipment/assignment" element={<EquipmentAssignment me={me} />} />
        <Route path="/equipment/:id" element={<EquipmentRecord me={me} />} />
        {/* Phase 7 (§6.9). */}
        <Route path="/invoices" element={<Invoices me={me} />} />
        <Route path="/invoices/:id" element={<InvoiceRecord me={me} />} />
        {/* Phase 5 (§6.7). Purchase orders live under Purchasing rather than
            beside it, which is how the breadcrumb reads. */}
        <Route path="/purchasing" element={<Purchasing me={me} />} />
        <Route path="/purchasing/purchase-orders" element={<PurchaseOrders me={me} />} />
        {/* The nav offers these because the permission matrix allows them; the
            screens land in their own phases. */}
        <Route
          path="/:section"
          element={
            <div className="p-4">
              <EmptyState
                title="Not built yet"
                body="This part of Rowted lands in a later phase. Contacts and properties are ready now."
                action={{ label: 'Go to contacts', onClick: () => navigate('/contacts') }}
              />
            </div>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* A new record opens its own profile: the next thing anyone does with a
          contact is link it to a property, and that lives on the profile. */}
      <ContactForm
        open={creating === 'contact'}
        onClose={() => setCreating(null)}
        orgUnitId={me.scope.selected[0] ?? ''}
        onSaved={(id, again) => {
          // "Save & add another" keeps the sheet open for the next one; a
          // plain save opens the profile, because linking a property is the
          // next thing anybody does with a contact.
          if (again) return
          setCreating(null)
          navigate(`/contacts/${id}`)
        }}
        onOpenExisting={(id) => { setCreating(null); navigate(`/contacts/${id}`) }}
      />

      <JobForm
        open={creating === 'job'}
        onClose={() => setCreating(null)}
        onSaved={(id) => { setCreating(null); navigate(`/jobs/${id}`) }}
        onSavedAndQuote={(jobId, quoteId) => {
          setCreating(null)
          navigate(quoteId ? `/quotes/${quoteId}` : `/jobs/${jobId}`)
        }}
      />

      <EquipmentForm
        open={creating === 'equipment'}
        onClose={() => setCreating(null)}
        orgUnitId={me.scope.selected[0] ?? ''}
        onSaved={(id, again) => {
          // Same rule as the others: "Save & add another" stays put.
          if (again) return
          setCreating(null)
          navigate(`/equipment/${id}`)
        }}
      />

      <PropertyForm
        open={creating === 'property'}
        onClose={() => setCreating(null)}
        orgUnitId={me.scope.selected[0] ?? ''}
        onSaved={(id, again) => {
          // Same as a contact: "Save & add another" keeps the sheet open, a
          // plain save opens the profile — where the measurements, the
          // contacts and the map are.
          if (again) return
          setCreating(null)
          navigate(`/properties/${id}`)
        }}
        onOpenExisting={(id) => { setCreating(null); navigate(`/properties/${id}`) }}
      />
      {/*
        * New request (§13.5): one sheet, opened from every entry point. After
        * Create the page stays where it was and its list reloads; *Create,
        * book later* goes to the job (§11.4).
        */}
      <NewRequestSheet
        open={creating === 'request'}
        prefill={prefill}
        onClose={() => { setCreating(null); setPrefill(null) }}
        onDone={(outcome) => {
          setCreating(null)
          setPrefill(null)
          setReloadSignal((value) => value + 1)
          setToast({ ...outcome, key: Date.now() })
          if (outcome.landing) navigate(outcome.landing)
          window.dispatchEvent(new CustomEvent(REQUEST_CREATED_EVENT, { detail: outcome }))
        }}
      />
      {toast && (
        <RequestToast
          key={toast.key}
          title={toast.toast.title}
          sub={toast.toast.sub}
          onOpenJob={toast.toast.openJob ? () => { setToast(null); navigate(`/jobs/${toast.jobId}`) } : undefined}
          onNewRequest={() => { setToast(null); startRequest() }}
          onDismiss={() => setToast(null)}
        />
      )}
    </AppFrame>
  )
}
